import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer
} from 'recharts';
import { Users, AlertTriangle, ChevronRight, ChevronLeft, ChevronDown, ChevronUp, ChevronsUpDown, List, BarChart3, Trophy, Star, Clock, Search, X } from 'lucide-react';
import {
  DEFAULT_STAR_FORMULA, DEFAULT_NEEDS_ATTENTION, normalizeFormula, normalizeThresholds,
  computeStarScore, formulaPreview,
} from '../lib/starScore';
import { useSortableTable } from '../lib/useSortableTable';
import {
  resolveRubricDimensions, getDimensionRatings, ratingToScore, QUALITY_SELECT_COLUMNS,
} from '../lib/qualityDimensions';
import { defaultPolicy } from '../lib/issuePolicy';
import { ISSUE_SELECT_COLUMNS, countOpenIssues } from '../lib/issueCounting';
import { loadTutorReviewStates, TUTOR_REVIEW_LABELS } from '../lib/tutorReview';

// Recharts' radar degenerates below three axes, so small rubrics fall back to
// the bar list instead of rendering a broken chart.
const MIN_RADAR_AXES = 3;

const timeRangeOptions = [
  { label: '7d', value: 7 },
  { label: '28d', value: 28 },
  { label: '90d', value: 90 },
  { label: '1y', value: 365 },
  { label: 'All', value: null },
];

const SUBTABS = [
  { key: 'all', label: 'View All' },
  { key: 'stars', label: 'Star Performers' },
  { key: 'attention', label: 'Needs Attention' },
  { key: 'under_review', label: 'Under Review' },
  { key: 'resolved', label: 'Resolved' },
];
// Subtabs that render the standard tutor list, filtered by review state (lib/tutorReview.js).
const LIST_SUBTABS = ['all', 'under_review', 'resolved'];

// Org-defined dimension labels can be long; keep radar axis text from colliding.
const shortLabel = (s, max = 16) =>
  typeof s === 'string' && s.length > max ? `${s.slice(0, max - 1)}…` : s;

// Fallback presentation when a rubric is too small for a readable radar.
const DimensionBars = ({ data, color, isDark }) => (
  <div className="h-full flex flex-col justify-center gap-2">
    {data.map(d => (
      <div key={d.dimension} className="flex items-center gap-2">
        <span className={`text-[11px] flex-1 min-w-0 truncate ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`} title={d.dimension}>
          {d.dimension}
        </span>
        <div className={`w-20 h-1.5 rounded-full overflow-hidden flex-shrink-0 ${isDark ? 'bg-[#27272a]' : 'bg-[#e5e5e5]'}`}>
          <div className="h-full rounded-full" style={{ width: `${d.value}%`, backgroundColor: color }} />
        </div>
        <span className={`text-[11px] w-9 text-right flex-shrink-0 ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{d.value}%</span>
      </div>
    ))}
  </div>
);

const MEDAL_COLORS = [
  { bg: '#FFD700', text: '#a16207', label: '1st' },
  { bg: '#C0C0C0', text: '#52525b', label: '2nd' },
  { bg: '#CD7F32', text: '#9a3412', label: '3rd' },
];

const Tutors = () => {
  const { organizationId } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === 'dark';
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listPage, setListPage] = useState(0);
  const [starPage, setStarPage] = useState(0);
  const [attentionPage, setAttentionPage] = useState(0);
  const [viewMode, setViewMode] = useState('radar');
  const [timeRange, setTimeRange] = useState(null);
  const [subtab, setSubtab] = useState('all');
  const [expandedTutor, setExpandedTutor] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [starFormula, setStarFormula] = useState(DEFAULT_STAR_FORMULA);
  const [thresholds, setThresholds] = useState(DEFAULT_NEEDS_ATTENTION);
  // The org's teaching-quality rubric; defaults to the five built-in dimensions.
  const [rubricDims, setRubricDims] = useState(() => resolveRubricDimensions(null));
  // { [tutor_name]: 'under_review' | 'open' | 'resolved' }, across all time
  const [reviewStates, setReviewStates] = useState({});

  useEffect(() => {
    if (!organizationId) return;
    loadTutors();
  }, [organizationId, timeRange]);

  const loadTutors = async () => {
    setLoading(true);
    try {
      const { data: orgRow } = await supabase
        .from('organizations')
        .select('star_score_formula, needs_attention_thresholds, quality_rubric, issue_filter_policy, lateness_minor_minutes')
        .eq('id', organizationId)
        .single();
      const formula = normalizeFormula(orgRow?.star_score_formula);
      const thresh = normalizeThresholds(orgRow?.needs_attention_thresholds);
      const dims = resolveRubricDimensions(orgRow?.quality_rubric);
      const issuePolicy = orgRow?.issue_filter_policy || defaultPolicy();
      setStarFormula(formula);
      setThresholds(thresh);
      setRubricDims(dims);
      loadTutorReviewStates(organizationId, issuePolicy).then(setReviewStates);

      let sessQuery = supabase
        .from('sessions')
        .select('id, tutor_name, duration_minutes, student_name, tutor_join_lateness_seconds, lateness_seconds')
        .eq('organization_id', organizationId)
        .eq('is_processed_v2', true);

      if (timeRange) {
        const d = new Date();
        d.setDate(d.getDate() - timeRange);
        sessQuery = sessQuery.gte('lesson_timestamp', d.toISOString());
      }

      const { data: sessions } = await sessQuery;
      const sessData = sessions || [];
      const sessionIds = sessData.map(s => s.id);

      // Batch .in() calls to avoid URL length limits with large session counts,
      // and page each batch because PostgREST caps a response at 1000 rows.
      const PAGE_SIZE = 1000;
      const batchIn = async (table, select, ids, batchSize = 200) => {
        if (!Array.isArray(ids) || ids.length === 0) return [];
        const results = [];
        for (let i = 0; i < ids.length; i += batchSize) {
          const chunk = ids.slice(i, i + batchSize);
          for (let from = 0; ; from += PAGE_SIZE) {
            const { data, error } = await supabase
              .from(table)
              .select(select)
              .in('session_id', chunk)
              .range(from, from + PAGE_SIZE - 1);
            if (error || !data) break;
            results.push(...data);
            if (data.length < PAGE_SIZE) break;
          }
        }
        return results;
      };

      const [qaData, risksData] = await Promise.all([
        batchIn('quality_assessments', `session_id, insufficient_evidence, ${QUALITY_SELECT_COLUMNS}`, sessionIds),
        // ISSUE_SELECT_COLUMNS carries category + filter_status, which the
        // shared issue rules in lib/issueCounting.js need.
        batchIn('risks_v2', ISSUE_SELECT_COLUMNS, sessionIds),
      ]);

      const qaBySession = {};
      // Skip abstaining assessments (insufficient transcript evidence) — they must not drag averages down
      qaData.forEach(qa => { if (!qa.insufficient_evidence) qaBySession[qa.session_id] = qa; });
      const risksBySession = {};
      risksData.forEach(r => {
        if (!r) return;
        if (!risksBySession[r.session_id]) risksBySession[r.session_id] = [];
        risksBySession[r.session_id].push(r);
      });

      const map = {};
      sessData.forEach(s => {
        const name = s.tutor_name;
        if (!name) return;
        if (!map[name]) {
          const dimSums = {};
          dims.forEach(d => { dimSums[d.key] = 0; });
          map[name] = { name, sessions: 0, totalMinutes: 0, students: new Set(), qaScores: [], openIssues: 0, onTimeCount: 0, lateCount: 0, dimSums, qaCount: 0 };
        }
        map[name].sessions++;
        map[name].totalMinutes += s.duration_minutes || 0;
        if (s.student_name) map[name].students.add(s.student_name);

        const qa = qaBySession[s.id];
        if (qa) {
          const ratings = getDimensionRatings(qa);
          const scores = dims.map(d => ratingToScore(ratings[d.key])).filter(x => x > 0);
          if (scores.length > 0) map[name].qaScores.push(scores.reduce((a, b) => a + b, 0) / scores.length);
          dims.forEach(d => {
            map[name].dimSums[d.key] = (map[name].dimSums[d.key] || 0) + ratingToScore(ratings[d.key]);
          });
          map[name].qaCount++;
        }

        const lateSec = s.tutor_join_lateness_seconds || s.lateness_seconds || 0;
        // Late from the minor-lateness threshold (default 3 min), as on the Punctuality page.
        const lateThresholdSec = (orgRow?.lateness_minor_minutes ?? 3) * 60;
        if (lateSec >= lateThresholdSec) map[name].lateCount++;
        else map[name].onTimeCount++;

        // Single source of truth for "flagged issue": lib/issueCounting.js.
        // Excludes tutor_lateness (its own Punctuality surfaces) and
        // guardrail-suppressed risks, so this matches TutorDetail and Issues.
        map[name].openIssues += countOpenIssues(risksBySession[s.id], issuePolicy);
      });

      const pct = (val, count) => count > 0 ? Math.round((val / count / 3) * 100) : 0;
      const maxHours = Math.max(...Object.values(map).map(t => t.totalMinutes / 60), 1);

      const list = Object.values(map).map(t => {
        const punctualityPct = t.sessions > 0 ? Math.round((t.onTimeCount / t.sessions) * 100) : 100;
        // key -> 0-100, one entry per rubric dimension
        const dimPcts = {};
        dims.forEach(d => { dimPcts[d.key] = pct(t.dimSums[d.key] || 0, t.qaCount); });
        const radarData = [
          { dimension: 'Punctuality', value: punctualityPct },
          ...dims.map(d => ({ dimension: d.label, value: dimPcts[d.key] })),
        ];
        const overallPct = Math.round(radarData.reduce((s, d) => s + d.value, 0) / radarData.length);
        const tierColor = overallPct >= 75 ? '#10b981' : overallPct >= 50 ? '#eab308' : overallPct >= 25 ? '#f97316' : '#ef4444';

        const qualityPct = t.qaScores.length > 0 ? Math.round((t.qaScores.reduce((a, b) => a + b, 0) / t.qaScores.length / 3) * 100) : 0;
        const issueRate = t.sessions > 0 ? t.openIssues / t.sessions : 0;
        const starScore = computeStarScore({
          dimensions: dimPcts,
          qaCount: t.qaCount,
          volumeShare: (t.totalMinutes / 60) / maxHours,
          issueRate,
          onTimePct: punctualityPct,
        }, formula);

        return {
          name: t.name,
          sessions: t.sessions,
          hours: Math.round(t.totalMinutes / 60 * 10) / 10,
          students: t.students.size,
          qualityPct,
          openIssues: t.openIssues,
          issueRate: Math.round(issueRate * 100),
          lateCount: t.lateCount,
          punctualityPct,
          radarData,
          overallPct,
          tierColor,
          starScore,
        };
      }).sort((a, b) => b.sessions - a.sessions);

      setTutors(list);
    } catch (err) {
      console.error('Error loading tutors:', err);
    }
    setLoading(false);
  };

  const cardClass = `rounded-lg border ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`;
  // A 1- or 2-dimension rubric can't fill a radar, so show the bar list instead.
  const canRenderRadar = rubricDims.length >= MIN_RADAR_AXES;

  const q = searchQuery.trim().toLowerCase();
  const matchesQuery = (name) => !q || name.toLowerCase().includes(q);

  const qualityCutoff = thresholds.quality_below_pct;
  const onTimeCutoff = thresholds.on_time_rate_below_pct;
  const minOpenIssues = thresholds.min_open_issues;

  const starRanked = [...tutors].sort((a, b) => b.starScore - a.starScore);
  const needsAttention = tutors.filter(t =>
    (minOpenIssues > 0 && t.openIssues >= minOpenIssues) ||
    (t.qualityPct > 0 && t.qualityPct < qualityCutoff) ||
    t.punctualityPct < onTimeCutoff
  ).sort((a, b) => {
    if (b.openIssues !== a.openIssues) return b.openIssues - a.openIssues;
    return a.overallPct - b.overallPct;
  });

  const attentionWithIssues = tutors.filter(t => minOpenIssues > 0 && t.openIssues >= minOpenIssues).length;
  const attentionLowQuality = tutors.filter(t => t.qualityPct > 0 && t.qualityPct < qualityCutoff).length;
  const attentionFreqLate = tutors.filter(t => t.punctualityPct < onTimeCutoff).length;

  const PER_PAGE = 20;
  const STAR_PER_PAGE = 10;
  const ATTN_PER_PAGE = 10;
  const isListSubtab = LIST_SUBTABS.includes(subtab);
  const filteredTutors = isListSubtab
    ? tutors.filter(t => matchesQuery(t.name) && (subtab === 'all' || reviewStates[t.name] === subtab))
    : [];
  const reviewCount = (state) => tutors.filter(t => reviewStates[t.name] === state).length;
  const { sortedRows: displayTutors, sortKey, sortDir, toggleSort, setSort } = useSortableTable({
    rows: filteredTutors,
    defaultKey: null,
    defaultDir: 'desc',
    firstDir: { name: 'asc', sessions: 'desc', hours: 'desc', qualityPct: 'desc', openIssues: 'desc' },
  });
  const totalPages = Math.ceil(displayTutors.length / PER_PAGE);
  const pagedTutors = displayTutors.slice(listPage * PER_PAGE, (listPage + 1) * PER_PAGE);

  useEffect(() => { setListPage(0); }, [sortKey, sortDir]);

  const SortIndicator = ({ k }) => {
    if (sortKey !== k) return <ChevronsUpDown className="h-3 w-3 opacity-0 group-hover:opacity-60 transition-opacity" />;
    return sortDir === 'asc'
      ? <ChevronUp className="h-3 w-3" />
      : <ChevronDown className="h-3 w-3" />;
  };
  const sortBtnClass = `group inline-flex items-center gap-1 text-xs font-medium uppercase tracking-wider transition-colors ${isDark ? 'hover:text-[#a1a1aa]' : 'hover:text-[#666]'}`;

  // Star performers: when not searching, show podium top 3 + rest below.
  // When searching, hide podium and show matching tutors with their TRUE ranks.
  const podiumTutors = q ? [] : starRanked.slice(0, Math.min(3, starRanked.length));
  const podiumOrder = podiumTutors.length >= 3
    ? [podiumTutors[1], podiumTutors[0], podiumTutors[2]]
    : podiumTutors;
  // rankedRest entries carry their true rank for display
  const rankedRest = q
    ? starRanked.map((t, i) => ({ ...t, _rank: i + 1 })).filter(t => matchesQuery(t.name))
    : starRanked.slice(3).map((t, i) => ({ ...t, _rank: i + 4 }));
  const starTotalPages = Math.max(1, Math.ceil(rankedRest.length / STAR_PER_PAGE));
  const safeStarPage = Math.min(starPage, starTotalPages - 1);
  const pagedRankedRest = rankedRest.slice(safeStarPage * STAR_PER_PAGE, (safeStarPage + 1) * STAR_PER_PAGE);

  const filteredNeedsAttention = needsAttention.filter(t => matchesQuery(t.name));
  const attnTotalPages = Math.max(1, Math.ceil(filteredNeedsAttention.length / ATTN_PER_PAGE));
  const safeAttnPage = Math.min(attentionPage, attnTotalPages - 1);
  const pagedNeedsAttention = filteredNeedsAttention.slice(safeAttnPage * ATTN_PER_PAGE, (safeAttnPage + 1) * ATTN_PER_PAGE);

  const handleSubtabChange = (key) => {
    setSubtab(key);
    setListPage(0);
    setStarPage(0);
    setAttentionPage(0);
    setExpandedTutor(null);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className={`text-2xl font-semibold font-[Manrope] tracking-tight ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
          Tutors
        </h1>
        <div className="flex items-center gap-3 flex-wrap">
          <div className={`relative flex items-center rounded-lg border ${isDark ? 'border-[#27272a] bg-[#121212]' : 'border-[#e5e5e5] bg-white'}`}>
            <Search className={`absolute left-2.5 h-3.5 w-3.5 pointer-events-none ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setListPage(0); setStarPage(0); setAttentionPage(0); }}
              placeholder="Search tutors"
              className={`pl-8 pr-7 py-1.5 text-sm rounded-lg bg-transparent outline-none w-44 ${isDark ? 'text-white placeholder:text-[#52525b]' : 'text-[#1a1a1a] placeholder:text-[#999]'}`}
            />
            {searchQuery && (
              <button
                onClick={() => { setSearchQuery(''); setListPage(0); setStarPage(0); setAttentionPage(0); }}
                className={`absolute right-1.5 p-0.5 rounded ${isDark ? 'text-[#52525b] hover:text-white' : 'text-[#999] hover:text-[#1a1a1a]'}`}
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <div className={`flex items-center rounded-lg border p-1 gap-0.5 ${isDark ? 'border-[#27272a] bg-[#121212]' : 'border-[#e5e5e5] bg-white'}`}>
            {timeRangeOptions.map(opt => (
              <button
                key={opt.label}
                onClick={() => { setTimeRange(opt.value); setListPage(0); }}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  timeRange === opt.value
                    ? isDark ? 'bg-[#27272a] text-white' : 'bg-[#e8e8e8] text-[#1a1a1a]'
                    : isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666666] hover:text-[#1a1a1a]'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {isListSubtab && viewMode === 'radar' && (
            <select
              value={sortKey ? `${sortKey}:${sortDir}` : ''}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) { setSort(null); return; }
                const [k, d] = v.split(':');
                setSort(k, d);
              }}
              className={`text-xs rounded-lg border px-3 py-2 ${isDark ? 'border-[#27272a] bg-[#121212] text-[#a1a1aa]' : 'border-[#e5e5e5] bg-white text-[#666]'}`}
              title="Sort tutors"
            >
              <option value="">Sort: default</option>
              <option value="name:asc">Name (A→Z)</option>
              <option value="name:desc">Name (Z→A)</option>
              <option value="sessions:desc">Sessions (high→low)</option>
              <option value="sessions:asc">Sessions (low→high)</option>
              <option value="hours:desc">Hours (high→low)</option>
              <option value="hours:asc">Hours (low→high)</option>
              <option value="qualityPct:desc">Quality (high→low)</option>
              <option value="qualityPct:asc">Quality (low→high)</option>
              <option value="openIssues:desc">Issues (high→low)</option>
              <option value="openIssues:asc">Issues (low→high)</option>
            </select>
          )}
          {isListSubtab && (
            <div className={`flex items-center rounded-lg border p-1 gap-0.5 ${isDark ? 'border-[#27272a] bg-[#121212]' : 'border-[#e5e5e5] bg-white'}`}>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? isDark ? 'bg-[#27272a] text-white' : 'bg-[#e8e8e8] text-[#1a1a1a]' : isDark ? 'text-[#52525b] hover:text-white' : 'text-[#999] hover:text-[#1a1a1a]'}`}
              >
                <List className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('radar')}
                className={`p-1.5 rounded-md transition-colors ${viewMode === 'radar' ? isDark ? 'bg-[#27272a] text-white' : 'bg-[#e8e8e8] text-[#1a1a1a]' : isDark ? 'text-[#52525b] hover:text-white' : 'text-[#999] hover:text-[#1a1a1a]'}`}
              >
                <BarChart3 className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Subtabs */}
      <div className="flex gap-4">
        {SUBTABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => handleSubtabChange(tab.key)}
            className={`text-sm font-medium flex items-center gap-1.5 pb-1 transition-colors ${
              subtab === tab.key
                ? isDark ? 'text-white border-b-2 border-[#586f8d]' : 'text-[#1a1a1a] border-b-2 border-[#586f8d]'
                : isDark ? 'text-[#52525b] hover:text-[#a1a1aa]' : 'text-[#999999] hover:text-[#666666]'
            }`}
          >
            {tab.label}
            {tab.key === 'attention' && filteredNeedsAttention.length > 0 && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                subtab === tab.key ? 'bg-red-500/20 text-red-400' : isDark ? 'bg-[#27272a] text-[#52525b]' : 'bg-[#f0f0f0] text-[#999]'
              }`}>{filteredNeedsAttention.length}</span>
            )}
            {(tab.key === 'under_review' || tab.key === 'resolved') && !loading && reviewCount(tab.key) > 0 && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                subtab === tab.key ? 'bg-sky-500/20 text-sky-500' : isDark ? 'bg-[#27272a] text-[#52525b]' : 'bg-[#f0f0f0] text-[#999]'
              }`}>{reviewCount(tab.key)}</span>
            )}
            {tab.key === 'all' && (
              <span className={`text-xs px-1.5 py-0.5 rounded ${
                subtab === tab.key
                  ? isDark ? 'bg-[#27272a] text-white' : 'bg-[#e8e8e8] text-[#1a1a1a]'
                  : isDark ? 'bg-[#18181b] text-[#52525b]' : 'bg-[#f0f0f0] text-[#999]'
              }`}>{loading ? <span className="inline-block w-4 h-3 align-middle rounded bg-current opacity-20 animate-pulse" /> : q ? `${displayTutors.length}/${tutors.length}` : tutors.length}</span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3 animate-pulse">
          {[1,2,3,4,5].map(i => <div key={i} className={`${cardClass} h-16`} />)}
        </div>
      ) : tutors.length === 0 ? (
        <div className={`text-center py-12 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
          <Users className="h-8 w-8 mx-auto mb-3 opacity-50" />
          <p className="text-sm">No tutor data available</p>
        </div>

      /* ========== STAR PERFORMERS ========== */
      ) : subtab === 'stars' ? (
        <div className="space-y-6">
          {/* Podium */}
          {podiumTutors.length > 0 && (
            <div className="p-6 pb-0 overflow-hidden">
              <div className={`flex items-end justify-center ${podiumTutors.length >= 3 ? 'gap-3 md:gap-5' : 'gap-6'}`}>
                {podiumOrder.map((t, visualIdx) => {
                  const rank = podiumTutors.length >= 3
                    ? (visualIdx === 1 ? 0 : visualIdx === 0 ? 1 : 2)
                    : visualIdx;
                  const medal = MEDAL_COLORS[rank];
                  const isFirst = rank === 0;
                  const podiumH = isFirst ? 'h-32' : rank === 1 ? 'h-24' : 'h-20';
                  const avatarSize = isFirst ? 'w-16 h-16' : 'w-12 h-12';

                  return (
                    <button
                      key={t.name}
                      onClick={() => navigate(`/tutors/${encodeURIComponent(t.name)}`)}
                      className="flex flex-col items-center group"
                    >
                      {/* Avatar */}
                      <div
                        className={`${avatarSize} rounded-full flex items-center justify-center mb-2 transition-transform group-hover:scale-105 ${isDark ? 'bg-[#27272a]' : 'bg-[#f0f0f0]'}`}
                        style={{ border: `${isFirst ? 3 : 2}px solid ${medal.bg}` }}
                      >
                        <span className={`${isFirst ? 'text-lg' : 'text-sm'} font-semibold ${isDark ? 'text-[#e4e4e7]' : 'text-[#444]'}`}>
                          {t.name.charAt(0)}
                        </span>
                      </div>

                      {/* Name */}
                      <span className={`text-sm font-medium truncate max-w-[100px] ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
                        {t.name}
                      </span>

                      {/* Score */}
                      <span className={`text-xs font-medium mt-0.5 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                        {t.starScore} pts
                      </span>

                      {/* Mini quality bar */}
                      <div className={`w-16 h-1.5 rounded-full mt-1.5 overflow-hidden ${isDark ? 'bg-[#27272a]' : 'bg-[#e5e5e5]'}`}>
                        <div className="h-full rounded-full" style={{ width: `${t.qualityPct}%`, backgroundColor: medal.bg }} />
                      </div>

                      {/* Podium block */}
                      <div
                        className={`w-24 md:w-28 ${podiumH} mt-3 rounded-t-lg flex flex-col items-center justify-start pt-3`}
                        style={{ backgroundColor: `${medal.bg}${isDark ? '26' : '33'}`, border: `1px solid ${medal.bg}80`, borderBottom: 'none' }}
                      >
                        <Trophy className="h-5 w-5" style={{ color: isDark ? medal.bg : medal.text }} />
                        <span className="text-xs font-bold mt-1" style={{ color: isDark ? medal.bg : medal.text }}>
                          {medal.label} place
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Score formula */}
          <div className={`flex items-center justify-center gap-1 text-xs text-center px-4 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
            {formulaPreview(starFormula)}
          </div>

          {/* Ranked list (4th onwards) */}
          {rankedRest.length > 0 && (
            <div className={cardClass}>
              <div className={`grid grid-cols-7 gap-4 px-5 py-3 text-xs font-medium uppercase tracking-wider ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                <span className="col-span-2">Tutor</span>
                <span>Rank</span>
                <span>Score</span>
                <span>Quality</span>
                <span>Hours</span>
                <span></span>
              </div>
              <div className={`divide-y ${isDark ? 'divide-[#27272a]' : 'divide-[#e5e5e5]'}`}>
                {pagedRankedRest.map((t) => (
                  <button
                    key={t.name}
                    onClick={() => navigate(`/tutors/${encodeURIComponent(t.name)}`)}
                    className={`w-full grid grid-cols-7 gap-4 px-5 py-4 items-center text-left transition-colors ${isDark ? 'hover:bg-[#18181b]/50' : 'hover:bg-[#fafafa]'}`}
                  >
                    <div className="col-span-2 flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-medium ${isDark ? 'bg-[#27272a] text-[#a1a1aa]' : 'bg-[#f0f0f0] text-[#666]'}`}>
                        {t._rank}
                      </div>
                      <span className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{t.name}</span>
                    </div>
                    <span className={`text-sm ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>#{t._rank}</span>
                    <div className="flex items-center gap-2">
                      <div className={`w-12 h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-[#27272a]' : 'bg-[#e5e5e5]'}`}>
                        <div className="h-full rounded-full accent-gradient" style={{ width: `${t.starScore}%` }} />
                      </div>
                      <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{t.starScore}</span>
                    </div>
                    <span className={`text-sm ${
                      t.qualityPct >= 75 ? isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'
                      : t.qualityPct >= 50 ? isDark ? 'text-[#a1a1aa]' : 'text-[#666]'
                      : t.qualityPct > 0 ? 'text-amber-400'
                      : isDark ? 'text-[#52525b]' : 'text-[#999]'
                    }`}>{t.qualityPct > 0 ? `${t.qualityPct}%` : '—'}</span>
                    <span className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{t.hours}h</span>
                    <div className="flex justify-end">
                      <ChevronRight className={`h-4 w-4 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`} />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {starTotalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <button onClick={() => setStarPage(Math.max(0, safeStarPage - 1))} disabled={safeStarPage === 0}
                className={`p-2 rounded-lg ${isDark ? 'hover:bg-[#18181b] disabled:opacity-30' : 'hover:bg-[#f0f0f0] disabled:opacity-30'}`}>
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{safeStarPage + 1} of {starTotalPages}</span>
              <button onClick={() => setStarPage(Math.min(starTotalPages - 1, safeStarPage + 1))} disabled={safeStarPage >= starTotalPages - 1}
                className={`p-2 rounded-lg ${isDark ? 'hover:bg-[#18181b] disabled:opacity-30' : 'hover:bg-[#f0f0f0] disabled:opacity-30'}`}>
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

      /* ========== NEEDS ATTENTION ========== */
      ) : subtab === 'attention' ? (
        filteredNeedsAttention.length === 0 ? (
          <div className={`text-center py-12 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
            <Users className="h-8 w-8 mx-auto mb-3 opacity-50" />
            <p className="text-sm">{q ? 'No tutors match your search' : 'No tutors need attention right now'}</p>
            {!q && <p className={`text-xs mt-1 ${isDark ? 'text-[#3f3f46]' : 'text-[#ccc]'}`}>Tutors with open issues, low scores, or poor punctuality will appear here</p>}
          </div>
        ) : (
          <div className="space-y-5">
            {/* Summary stat cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className={`${cardClass} p-5`}>
                <div className="flex items-center justify-between">
                  <span className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>With Open Issues</span>
                  <AlertTriangle className="h-4 w-4 text-amber-500" strokeWidth={1.5} />
                </div>
                <div className={`text-3xl font-semibold mt-2 font-[Manrope] tracking-tight ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
                  {attentionWithIssues}
                </div>
                <div className={`text-xs mt-2 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                  tutors with unresolved issues
                </div>
              </div>
              <div className={`${cardClass} p-5`}>
                <div className="flex items-center justify-between">
                  <span className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Low Quality Score</span>
                  <Star className="h-4 w-4 text-red-400" strokeWidth={1.5} />
                </div>
                <div className={`text-3xl font-semibold mt-2 font-[Manrope] tracking-tight ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
                  {attentionLowQuality}
                </div>
                <div className={`text-xs mt-2 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                  tutors below {qualityCutoff}% quality
                </div>
              </div>
              <div className={`${cardClass} p-5`}>
                <div className="flex items-center justify-between">
                  <span className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Frequently Late</span>
                  <Clock className="h-4 w-4 text-red-400" strokeWidth={1.5} />
                </div>
                <div className={`text-3xl font-semibold mt-2 font-[Manrope] tracking-tight ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
                  {attentionFreqLate}
                </div>
                <div className={`text-xs mt-2 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                  tutors below {onTimeCutoff}% on-time rate
                </div>
              </div>
            </div>

            {/* Expandable tutor cards */}
            <div className="space-y-3">
              {pagedNeedsAttention.map(t => {
                const isExpanded = expandedTutor === t.name;
                const problems = [];
                if (minOpenIssues > 0 && t.openIssues >= minOpenIssues) problems.push({ label: `${t.openIssues} open issue${t.openIssues > 1 ? 's' : ''}`, color: 'text-amber-400 bg-amber-400/10' });
                if (t.qualityPct > 0 && t.qualityPct < qualityCutoff) problems.push({ label: 'Low quality', color: 'text-red-400 bg-red-400/10' });
                if (t.punctualityPct < onTimeCutoff) problems.push({ label: 'Frequently late', color: 'text-red-400 bg-red-400/10' });
                if (t.overallPct < qualityCutoff) problems.push({ label: 'Low overall', color: 'text-orange-400 bg-orange-400/10' });

                return (
                  <div key={t.name} className={cardClass}>
                    <button
                      onClick={() => setExpandedTutor(isExpanded ? null : t.name)}
                      className={`w-full flex items-center justify-between px-5 py-4 text-left transition-colors ${isDark ? 'hover:bg-[#18181b]/30' : 'hover:bg-[#fafafa]'}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div
                          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: t.tierColor + '20', color: t.tierColor }}
                        >
                          <span className="text-sm font-medium">{t.name.charAt(0)}</span>
                        </div>
                        <div className="min-w-0">
                          <span className={`text-sm font-medium block truncate ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{t.name}</span>
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            {problems.map((p, i) => (
                              <span key={i} className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${p.color}`}>
                                {p.label}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                        <span className={`text-sm font-medium ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                          {t.overallPct}%
                        </span>
                        {isExpanded
                          ? <ChevronUp className={`h-4 w-4 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`} />
                          : <ChevronDown className={`h-4 w-4 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`} />
                        }
                      </div>
                    </button>

                    {isExpanded && (
                      <div className={`px-5 pb-5 border-t ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                          {/* Mini radar chart (bar list for small rubrics) */}
                          <div className="h-48">
                            {canRenderRadar ? (
                              <ResponsiveContainer width="100%" height="100%">
                                <RadarChart data={t.radarData} cx="50%" cy="50%" outerRadius="70%">
                                  <PolarGrid stroke={isDark ? '#27272a' : '#e5e5e5'} />
                                  <PolarAngleAxis dataKey="dimension" tickFormatter={shortLabel} tick={{ fontSize: 10, fill: isDark ? '#71717a' : '#999' }} />
                                  <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} axisLine={false} />
                                  <Radar dataKey="value" stroke={t.tierColor} fill={t.tierColor} fillOpacity={0.2} strokeWidth={1.5} />
                                </RadarChart>
                              </ResponsiveContainer>
                            ) : (
                              <DimensionBars data={t.radarData} color={t.tierColor} isDark={isDark} />
                            )}
                          </div>

                          {/* Stats + action */}
                          <div className="flex flex-col justify-between">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <div className={`text-[10px] uppercase tracking-wider ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Sessions</div>
                                <div className={`text-sm font-medium mt-0.5 ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{t.sessions}</div>
                              </div>
                              <div>
                                <div className={`text-[10px] uppercase tracking-wider ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Hours</div>
                                <div className={`text-sm font-medium mt-0.5 ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{t.hours}h</div>
                              </div>
                              <div>
                                <div className={`text-[10px] uppercase tracking-wider ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Quality</div>
                                <div className="text-sm font-medium mt-0.5" style={{ color: t.tierColor }}>{t.qualityPct > 0 ? `${t.qualityPct}%` : '—'}</div>
                              </div>
                              <div>
                                <div className={`text-[10px] uppercase tracking-wider ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Punctuality</div>
                                <div className={`text-sm font-medium mt-0.5 ${t.punctualityPct < onTimeCutoff ? 'text-red-400' : isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
                                  {t.punctualityPct}%
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); navigate(`/tutors/${encodeURIComponent(t.name)}`); }}
                              className={`mt-4 w-full px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                                isDark
                                  ? 'border-[#27272a] text-[#a1a1aa] hover:text-white hover:border-[#3f3f46]'
                                  : 'border-[#e5e5e5] text-[#666] hover:text-[#1a1a1a] hover:border-[#d0d0d0]'
                              }`}
                            >
                              View Profile
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {attnTotalPages > 1 && (
              <div className="flex items-center justify-center gap-2">
                <button onClick={() => setAttentionPage(Math.max(0, safeAttnPage - 1))} disabled={safeAttnPage === 0}
                  className={`p-2 rounded-lg ${isDark ? 'hover:bg-[#18181b] disabled:opacity-30' : 'hover:bg-[#f0f0f0] disabled:opacity-30'}`}>
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{safeAttnPage + 1} of {attnTotalPages}</span>
                <button onClick={() => setAttentionPage(Math.min(attnTotalPages - 1, safeAttnPage + 1))} disabled={safeAttnPage >= attnTotalPages - 1}
                  className={`p-2 rounded-lg ${isDark ? 'hover:bg-[#18181b] disabled:opacity-30' : 'hover:bg-[#f0f0f0] disabled:opacity-30'}`}>
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        )

      /* ========== VIEW ALL ========== */
      ) : displayTutors.length === 0 ? (
        <div className={`text-center py-12 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
          <Users className="h-8 w-8 mx-auto mb-3 opacity-50" />
          <p className="text-sm">
            {q ? `No tutors match "${searchQuery}"`
              : subtab === 'under_review' ? 'No tutors are under review'
              : subtab === 'resolved' ? 'No tutors with resolved issues yet'
              : 'No tutors'}
          </p>
        </div>
      ) : viewMode === 'radar' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pagedTutors.map(t => (
            <button
              key={t.name}
              onClick={() => navigate(`/tutors/${encodeURIComponent(t.name)}`)}
              className={`${cardClass} p-4 text-left transition-all ${isDark ? 'hover:border-[#3f3f46]' : 'hover:border-[#ccc]'}`}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg accent-gradient flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-sm font-medium">{t.name.charAt(0)}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center min-w-0">
                    <span className={`text-sm font-medium block truncate ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{t.name}</span>
                    {(reviewStates[t.name] === 'under_review' || reviewStates[t.name] === 'resolved') && (
                      <span className={`ml-1.5 inline-flex flex-shrink-0 items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${
                        reviewStates[t.name] === 'under_review'
                          ? isDark ? 'bg-sky-500/15 text-sky-300' : 'bg-sky-50 text-sky-700'
                          : isDark ? 'bg-emerald-500/15 text-emerald-300' : 'bg-emerald-50 text-emerald-700'
                      }`}>{TUTOR_REVIEW_LABELS[reviewStates[t.name]]}</span>
                    )}
                  </div>
                  <div className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{t.sessions} sessions · {t.hours}h</div>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: t.tierColor }} />
                  <span className={`text-xs font-medium ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{t.overallPct}%</span>
                </div>
              </div>
              <div className="h-44">
                {canRenderRadar ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={t.radarData} cx="50%" cy="50%" outerRadius="70%">
                      <PolarGrid stroke={isDark ? '#27272a' : '#e5e5e5'} />
                      <PolarAngleAxis dataKey="dimension" tickFormatter={shortLabel} tick={{ fontSize: 9, fill: isDark ? '#71717a' : '#999' }} />
                      <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} axisLine={false} />
                      <Radar dataKey="value" stroke={t.tierColor} fill={t.tierColor} fillOpacity={0.2} strokeWidth={1.5} />
                    </RadarChart>
                  </ResponsiveContainer>
                ) : (
                  <DimensionBars data={t.radarData} color={t.tierColor} isDark={isDark} />
                )}
              </div>
              <div className="grid grid-cols-3 gap-3 mt-1">
                <div>
                  <div className={`text-[10px] ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Quality</div>
                  <div className={`text-sm font-medium ${
                    t.qualityPct >= 75 ? isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'
                    : t.qualityPct >= 50 ? isDark ? 'text-[#a1a1aa]' : 'text-[#666]'
                    : t.qualityPct > 0 ? 'text-amber-400'
                    : isDark ? 'text-[#52525b]' : 'text-[#999]'
                  }`}>{t.qualityPct > 0 ? `${t.qualityPct}%` : '—'}</div>
                </div>
                <div>
                  <div className={`text-[10px] ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Students</div>
                  <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{t.students}</div>
                </div>
                <div>
                  <div className={`text-[10px] ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Issues</div>
                  <div className={`text-sm font-medium ${t.openIssues > 0 ? 'text-amber-400' : isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{t.openIssues}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <>
          <div className={cardClass}>
            <div className={`grid grid-cols-7 gap-4 px-5 py-3 text-xs font-medium uppercase tracking-wider ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
              <button onClick={() => toggleSort('name')} className={`col-span-2 ${sortBtnClass} justify-self-start`}>
                Tutor <SortIndicator k="name" />
              </button>
              <button onClick={() => toggleSort('sessions')} className={`${sortBtnClass} justify-self-start`}>
                Sessions <SortIndicator k="sessions" />
              </button>
              <button onClick={() => toggleSort('hours')} className={`${sortBtnClass} justify-self-start`}>
                Hours <SortIndicator k="hours" />
              </button>
              <button onClick={() => toggleSort('qualityPct')} className={`${sortBtnClass} justify-self-start`}>
                Quality <SortIndicator k="qualityPct" />
              </button>
              <button onClick={() => toggleSort('openIssues')} className={`${sortBtnClass} justify-self-start`}>
                Issues <SortIndicator k="openIssues" />
              </button>
              <span></span>
            </div>
            <div className={`divide-y ${isDark ? 'divide-[#27272a]' : 'divide-[#e5e5e5]'}`}>
              {pagedTutors.map(t => (
                <button
                  key={t.name}
                  onClick={() => navigate(`/tutors/${encodeURIComponent(t.name)}`)}
                  className={`w-full grid grid-cols-7 gap-4 px-5 py-4 items-center text-left transition-colors ${isDark ? 'hover:bg-[#18181b]/50' : 'hover:bg-[#fafafa]'}`}
                >
                  <div className="col-span-2 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg accent-gradient flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-sm font-medium">{t.name.charAt(0)}</span>
                    </div>
                    <span className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{t.name}</span>
                    {(reviewStates[t.name] === 'under_review' || reviewStates[t.name] === 'resolved') && (
                      <span className={`ml-1.5 inline-flex flex-shrink-0 items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${
                        reviewStates[t.name] === 'under_review'
                          ? isDark ? 'bg-sky-500/15 text-sky-300' : 'bg-sky-50 text-sky-700'
                          : isDark ? 'bg-emerald-500/15 text-emerald-300' : 'bg-emerald-50 text-emerald-700'
                      }`}>{TUTOR_REVIEW_LABELS[reviewStates[t.name]]}</span>
                    )}
                  </div>
                  <span className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{t.sessions}</span>
                  <span className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{t.hours}h</span>
                  <span className={`text-sm ${
                    t.qualityPct >= 75 ? isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'
                    : t.qualityPct >= 50 ? isDark ? 'text-[#a1a1aa]' : 'text-[#666]'
                    : t.qualityPct > 0 ? 'text-amber-400'
                    : isDark ? 'text-[#52525b]' : 'text-[#999]'
                  }`}>{t.qualityPct > 0 ? `${t.qualityPct}%` : '—'}</span>
                  <span className={`text-sm ${t.openIssues > 0 ? 'text-amber-400' : isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                    {t.openIssues > 0 && <AlertTriangle className="h-3 w-3 inline mr-1" />}{t.openIssues}
                  </span>
                  <div className="flex justify-end">
                    <ChevronRight className={`h-4 w-4 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`} />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <button onClick={() => setListPage(Math.max(0, listPage - 1))} disabled={listPage === 0}
            className={`p-2 rounded-lg ${isDark ? 'hover:bg-[#18181b] disabled:opacity-30' : 'hover:bg-[#f0f0f0] disabled:opacity-30'}`}>
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{listPage + 1} of {totalPages}</span>
          <button onClick={() => setListPage(Math.min(totalPages - 1, listPage + 1))} disabled={listPage >= totalPages - 1}
            className={`p-2 rounded-lg ${isDark ? 'hover:bg-[#18181b] disabled:opacity-30' : 'hover:bg-[#f0f0f0] disabled:opacity-30'}`}>
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default Tutors;
